<script>
import { mapActions, mapGetters } from 'vuex';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { CARD_CLASS, FIELD_CLASS, SELECT_CLASS } from '../constants';
import { SLA_TIERS } from '../catalogForms';
import { resolveQuery } from '../routingForm';

// "Who would get this ticket?" The answer is the backend's: this only shows
// it, it never recomputes coverage or leave in the browser. `day` can be later
// than the asked date when nobody covers it (rolls forward, never for T0).
export default {
  name: 'ResolverPanel',
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      tiers: SLA_TIERS,
      form: { category: '', siteId: '', date: '', tier: '' },
      result: null,
      error: '',
      isResolving: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCatalog', { categories: 'getActiveCategories' }),
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
  },
  methods: {
    ...mapActions('coLivingRouting', ['resolve']),
    async onResolve() {
      this.error = '';
      this.result = null;
      if (!this.form.category) {
        this.error = this.$t('CO_LIVING.ROUTING.ERRORS.CATEGORY');
        return;
      }
      this.isResolving = true;
      try {
        this.result = await this.resolve(resolveQuery(this.form));
      } catch (err) {
        this.error = apiErrorMessage(
          this,
          err,
          'CO_LIVING.ROUTING.RESOLVE.FAILED'
        );
      } finally {
        this.isResolving = false;
      }
    },
  },
};
</script>

<template>
  <section :class="cardClass" data-testid="co-living-resolver">
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
      {{ $t('CO_LIVING.ROUTING.RESOLVE.TITLE') }}
    </h2>
    <p class="mt-1 mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('CO_LIVING.ROUTING.RESOLVE.DESCRIPTION') }}
    </p>

    <form class="flex flex-wrap items-end gap-3" @submit.prevent="onResolve">
      <div>
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('CO_LIVING.ROUTING.FIELDS.CATEGORY') }}
        </label>
        <select
          v-model="form.category"
          class="!w-48"
          :class="[selectClass]"
          data-testid="co-living-resolve-category"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.ROUTING.FIELDS.CATEGORY_PLACEHOLDER') }}
          </option>
          <option v-for="c in categories" :key="c.key" :value="c.key">
            {{ c.name }}
          </option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('CO_LIVING.ROUTING.FIELDS.CONDO') }}
        </label>
        <select
          v-model="form.siteId"
          class="!w-48"
          :class="[selectClass]"
          data-testid="co-living-resolve-condo"
        >
          <option value="">
            {{ $t('CO_LIVING.ROUTING.RESOLVE.ANY_CONDO') }}
          </option>
          <option v-for="condo in condos" :key="condo.id" :value="condo.id">
            {{ condo.name }}
          </option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.DATE') }}
        </label>
        <input
          v-model="form.date"
          type="date"
          class="!w-44"
          :class="[fieldClass]"
          data-testid="co-living-resolve-date"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.TIER') }}
        </label>
        <select
          v-model="form.tier"
          class="!w-40"
          :class="[selectClass]"
          data-testid="co-living-resolve-tier"
        >
          <option value="">
            {{ $t('CO_LIVING.ROUTING.RESOLVE.TIER_RULE') }}
          </option>
          <option v-for="tier in tiers" :key="tier" :value="tier">
            {{ tier }}
          </option>
        </select>
      </div>
      <woot-button
        type="submit"
        :is-loading="isResolving"
        data-testid="co-living-resolve-submit"
      >
        {{ $t('CO_LIVING.ROUTING.RESOLVE.ACTION') }}
      </woot-button>
    </form>

    <p
      v-if="error"
      class="mt-3 text-sm text-red-500"
      data-testid="co-living-resolve-error"
    >
      {{ error }}
    </p>

    <dl
      v-if="result"
      class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm"
      data-testid="co-living-resolve-result"
    >
      <div>
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.PERSON') }}
        </dt>
        <dd
          class="text-slate-900 dark:text-slate-100"
          data-testid="co-living-resolve-member"
        >
          <template v-if="result.member">
            {{ result.member.name }}
          </template>
          <span v-else class="text-red-600 dark:text-red-400">
            {{ $t('CO_LIVING.ROUTING.RESOLVE.NOBODY') }}
          </span>
        </dd>
      </div>
      <div>
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.ROLE') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">{{ result.role }}</dd>
      </div>
      <div v-if="result.via">
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.VIA') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{ $t(`CO_LIVING.ROUTING.RESOLVE.VIA_${result.via.toUpperCase()}`) }}
        </dd>
      </div>
      <div v-if="result.day">
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.DAY') }}
        </dt>
        <dd
          class="text-slate-900 dark:text-slate-100"
          data-testid="co-living-resolve-day"
        >
          {{ result.day }}
        </dd>
      </div>
      <div v-if="result.rule">
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.RULE') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{
            $t(`CO_LIVING.ROUTING.RESOLVE.RULE_${result.rule.toUpperCase()}`)
          }}
        </dd>
      </div>
      <div v-if="result.sla_tier">
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.TIER') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{ result.sla_tier }}
        </dd>
      </div>
      <div v-if="result.reason" class="md:col-span-2">
        <dt class="text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.ROUTING.RESOLVE.REASON') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">{{ result.reason }}</dd>
      </div>
    </dl>
  </section>
</template>
